import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, isDevMode } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, tap, throwError } from 'rxjs';
import { AlertService } from '@core/services/alert.service';
import { ConnectivityService } from '@core/services/connectivity.service';
import { environment } from '../../../environments/environment';
import { SubscriptionAccessService } from '@core/services/subscription-access.service';

const GENERIC_SERVER_ERROR_MESSAGE = 'Ocurrió un problema con el servidor. Inténtalo de nuevo.';
const NETWORK_ERROR_CODE = 'NETWORK_ERROR';
const USER_FACING_ERROR_MESSAGES: Record<string, string> = {
  FIREBASE_UID_MISMATCH: 'Hubo un problema con tu cuenta. Contacta a soporte para recibir ayuda.',
  INTERNAL_SERVER_ERROR: GENERIC_SERVER_ERROR_MESSAGE,
};

const BLOCKED_REQUEST_ALLOWLIST = [
  '/subscription/',
  '/auth/login',
  '/auth/register',
  '/auth/activation/validate',
  '/auth/activation/complete',
  '/auth/password-reset/request',
  '/token/refreshToken',
  '/token/validateToken',
];

function isAllowedWhileSubscriptionBlocked(url: string): boolean {
  return BLOCKED_REQUEST_ALLOWLIST.some((allowedPath) =>
    url.includes(allowedPath),
  );
}

function resolveRedirectUrl(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  const candidate = String(value).trim();
  if (!candidate.startsWith('/')) {
    return null;
  }

  if (
    candidate.startsWith('/pricing') ||
    candidate.startsWith('/login') ||
    candidate.startsWith('/register') ||
    candidate.startsWith('/activate-account') ||
    candidate.startsWith('/reset-password')
  ) {
    return null;
  }

  return candidate;
}

function isPublicRoute(url: string): boolean {
  return (
    url.startsWith('/pricing') ||
    url.startsWith('/login') ||
    url.startsWith('/register') ||
    url.startsWith('/activate-account') ||
    url.startsWith('/reset-password')
  );
}

function navigateToPricing(router: Router, fromUrl: string): void {
  const redirectUrl = resolveRedirectUrl(fromUrl);
  void router.navigate(['/pricing'], {
    queryParams: redirectUrl ? { from: redirectUrl } : undefined,
  });
}

const FIELD_NAME_MAP: Record<string, string> = {
  name: 'nombre',
  description: 'descripción',
  contact_name: 'nombre del contacto',
  phone: 'teléfono',
  email: 'correo electrónico',
  address: 'dirección',
  rfc: 'RFC',
  social_link: 'red social',
  concept: 'concepto',
  notes: 'notas',
  sku: 'SKU',
  barcode: 'código de barras',
  image_url: 'URL de imagen',
  logo_url: 'logo',
  amount: 'monto',
};

function parseMysqlDataTooLong(message: string): string | null {
  const match = message.match(/Data too long for column '([^']+)'/i);
  if (!match) return null;
  const column = match[1];
  const fieldLabel = FIELD_NAME_MAP[column] ?? `'${column}'`;
  return `El campo ${fieldLabel} excede el máximo de caracteres permitido. Por favor acórtalo.`;
}

function parseNestValidationError(body: unknown): string | null {
  const parsed = body as { statusCode?: number; message?: unknown } | null;
  if (!parsed) return null;
  if (parsed.statusCode !== 400) return null;
  if (!Array.isArray(parsed.message) || parsed.message.length === 0) return null;
  const first = String(parsed.message[0]).trim();
  return first || null;
}

function normalizeBackendError(error: unknown): {
  status: string;
  message?: string;
  error: {
    code: string;
    details: Record<string, unknown>;
  };
} | null {
  const backendError = error as {
    status?: string;
    message?: string;
    code?: string;
    details?: Record<string, unknown>;
    error?: {
      code?: string;
      details?: Record<string, unknown>;
      message?: string;
    };
  } | null;

  const code = backendError?.error?.code ?? backendError?.code;

  if (!code) {
    return null;
  }

  return {
    status: backendError?.status ?? 'error',
    message:
      USER_FACING_ERROR_MESSAGES[code] ??
      backendError?.message ??
      backendError?.error?.message,
    error: {
      code,
      details: backendError?.error?.details ?? backendError?.details ?? {},
    },
  };
}

function buildNetworkError(
  url: string,
  method: string,
  original?: unknown,
): {
  code: number;
  status: string;
  message: string;
  error: {
    code: string;
    message: string;
    details: Record<string, unknown>;
  };
} {
  return {
    code: 0,
    status: 'error',
    message: GENERIC_SERVER_ERROR_MESSAGE,
    error: {
      code: NETWORK_ERROR_CODE,
      message: GENERIC_SERVER_ERROR_MESSAGE,
      details: { url, method, original: original ?? null },
    },
  };
}

function logDevHttpError(label: string, context: {
  method: string;
  url: string;
  status: number;
  statusText: string;
  original?: unknown;
}): void {
  if (!isDevMode()) {
    return;
  }

  console.error(`[HTTP_ERROR:${label}]`, {
    method: context.method,
    url: context.url,
    status: context.status,
    statusText: context.statusText,
    navigatorOnline: typeof navigator === 'undefined' ? 'n/a' : navigator.onLine,
    timestamp: new Date().toISOString(),
  });

  if (context.original) {
    console.error(`[HTTP_ERROR:${label}:original]`, context.original);
  }
}

export const httpInterceptorsInterceptor: HttpInterceptorFn = (req, next) => {
  const alertService = inject(AlertService);
  const connectivityService = inject(ConnectivityService);
  const router = inject(Router);
  const subscriptionAccessService = inject(SubscriptionAccessService);
  const blockedState = subscriptionAccessService.blockState();

  if (!connectivityService.isOnline()) {
    const browserOnline = typeof navigator === 'undefined' || navigator.onLine;

    // A failed request previously flipped the flag to offline even though the
    // browser still has connectivity (server unreachable, CORS, timeout). That
    // is NOT lost connectivity: short-circuiting here would block every request
    // (the only path to markOnline) and permanently stuck the app in offline mode.
    // Only hard-cut when the browser reports being truly offline.
    if (!browserOnline) {
      connectivityService.markOffline();
      logDevHttpError('OFFLINE', {
        method: req.method,
        url: req.url,
        status: 0,
        statusText: 'Sin conexión',
      });
      return throwError(() => buildNetworkError(req.url, req.method));
    }
  }

  if (blockedState && !isAllowedWhileSubscriptionBlocked(req.url)) {
    if (!isPublicRoute(router.url)) {
      navigateToPricing(router, router.url);
    }

    return throwError(() => ({
      code: 402,
      status: 'error',
      message: blockedState.message,
      error: {
        code: blockedState.code,
        details: blockedState.details ?? {},
      },
    }));
  }

  // IndexedDB owns offline records. A service-worker snapshot can hide local
  // writes or return the previous list while a slow network request finishes.
  const apiBase = environment.api_rest_url.replace(/\/$/, '');
  const resource = req.url.startsWith(`${apiBase}/`)
    ? req.url.slice(apiBase.length + 1).split(/[/?]/)[0]
    : '';
  const offlineResources = new Set([
    'categoryBusiness', 'brandBusiness', 'supplier', 'product', 'sale',
    'cashSession', 'cashMovement', 'inventoryMovement',
  ]);
  if (req.method === 'GET' && offlineResources.has(resource)) {
    req = req.clone({ params: req.params.set('ngsw-bypass', 'true') });
  }

  return next(req).pipe(
    tap(() => connectivityService.markOnline()),
    catchError((error: HttpErrorResponse) => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        return throwError(() => error);
      }

      // Network error (offline, server unreachable, CORS preflight failure).
      // The user only sees a generic message; callers surface it via error.error.message.
      if (error instanceof HttpErrorResponse && error.status === 0) {
        connectivityService.markNetworkFailure();
        logDevHttpError('NETWORK', {
          method: req.method,
          url: req.url,
          status: error.status,
          statusText: error.statusText,
          original: error,
        });
        return throwError(() => buildNetworkError(req.url, req.method, error));
      }

      const normalizedBackendError = normalizeBackendError(error.error);

      if (normalizedBackendError) {
        if (subscriptionAccessService.isSubscriptionBlockError(normalizedBackendError)) {
          subscriptionAccessService.setBlockedByError(normalizedBackendError, req.url);

          if (!isPublicRoute(router.url)) {
            navigateToPricing(router, router.url);
          }
        }

        return throwError(() => normalizedBackendError);
      }

      if (subscriptionAccessService.isSubscriptionBlockError(error)) {
        subscriptionAccessService.setBlockedByError(error, req.url);

        if (!isPublicRoute(router.url)) {
          navigateToPricing(router, router.url);
        }

        return throwError(() => error);
      }

      const errorBody = error.error as { message?: unknown; statusCode?: number } | null;

      if (typeof errorBody?.message === 'string') {
        const mysqlMsg = parseMysqlDataTooLong(errorBody.message);
        if (mysqlMsg) {
          alertService.open({ message: mysqlMsg, type: 'error' });
          return throwError(() => ({
            code: error.status || 500,
            message: mysqlMsg,
            status: 'error',
            error: { code: 'DATA_TOO_LONG', details: {} }
          }));
        }
      }

      const validationMsg = parseNestValidationError(error.error);
      if (validationMsg) {
        alertService.open({ message: validationMsg, type: 'error' });
        return throwError(() => ({
          code: 400,
          message: validationMsg,
          status: 'error',
          error: { code: 'VALIDATION_ERROR', details: {} }
        }));
      }

      alertService.open({ message: 'Ocurrió un error inesperado', type: 'error' });
      return throwError(() => ({
        code: error.status || 500,
        message: 'Ocurrió un error inesperado',
        status: 'error',
        error: {
          code: 'UNKNOWN_ERROR',
          details: {}
        }
      }));
    })
  );
};
